import { supabase } from '../../lib/supabase';

// ─── Types (mirror public.blog_* tables) ────────────────────────────────────
export interface BlogCategory {
  slug: string;
  display_name: string;
  is_localized: boolean;
  sort_order: number;
}

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  category_slug: string;
  content: string;
  excerpt: string | null;
  cover_image_url: string | null;
  status: 'draft' | 'published';
  published_at: string | null;
  meta_title: string | null;
  meta_description: string | null;
  author_name: string | null;
  created_at: string;
  updated_at: string;
}

export type BlogPostInput = Omit<
  BlogPost,
  'id' | 'created_at' | 'updated_at' | 'published_at'
>;

const POST_COLS =
  'id,title,slug,category_slug,content,excerpt,cover_image_url,status,published_at,meta_title,meta_description,author_name,created_at,updated_at';

// ─── Helpers ────────────────────────────────────────────────────────────────
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

// ─── Categories ─────────────────────────────────────────────────────────────
export async function listCategories(): Promise<BlogCategory[]> {
  const { data, error } = await supabase
    .from('blog_categories')
    .select('slug,display_name,is_localized,sort_order')
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

// ─── Posts ──────────────────────────────────────────────────────────────────
export async function listPosts(): Promise<BlogPost[]> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select(POST_COLS)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data as BlogPost[]) ?? [];
}

export async function getPost(id: string): Promise<BlogPost | null> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select(POST_COLS)
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data as BlogPost | null) ?? null;
}

export async function createPost(input: BlogPostInput): Promise<BlogPost> {
  const payload = {
    ...input,
    published_at: input.status === 'published' ? new Date().toISOString() : null,
  };
  const { data, error } = await supabase
    .from('blog_posts')
    .insert(payload)
    .select(POST_COLS)
    .single();
  if (error) throw error;
  return data as BlogPost;
}

export async function updatePost(
  id: string,
  input: BlogPostInput,
  opts: { existingPublishedAt: string | null },
): Promise<BlogPost> {
  // Preserve published_at across edits; set it when publishing for the first
  // time, keep it (for reference) when unpublishing.
  let published_at = opts.existingPublishedAt;
  if (input.status === 'published' && !published_at) {
    published_at = new Date().toISOString();
  }
  const { data, error } = await supabase
    .from('blog_posts')
    .update({ ...input, published_at })
    .eq('id', id)
    .select(POST_COLS)
    .single();
  if (error) throw error;
  return data as BlogPost;
}

export async function deletePost(id: string): Promise<void> {
  const { error } = await supabase.from('blog_posts').delete().eq('id', id);
  if (error) throw error;
}

export async function duplicatePost(post: BlogPost): Promise<BlogPost> {
  const base = slugify(post.title) || 'post';
  const copySlug = `${base}-copy-${Date.now().toString(36)}`;
  return createPost({
    title: `${post.title} (copy)`,
    slug: copySlug,
    category_slug: post.category_slug,
    content: post.content,
    excerpt: post.excerpt,
    cover_image_url: post.cover_image_url,
    status: 'draft',
    meta_title: post.meta_title,
    meta_description: post.meta_description,
    author_name: post.author_name,
  });
}

export async function setStatus(
  post: BlogPost,
  status: 'draft' | 'published',
): Promise<BlogPost> {
  let published_at = post.published_at;
  if (status === 'published' && !published_at) {
    published_at = new Date().toISOString();
  }
  const { data, error } = await supabase
    .from('blog_posts')
    .update({ status, published_at })
    .eq('id', post.id)
    .select(POST_COLS)
    .single();
  if (error) throw error;
  return data as BlogPost;
}

// ─── Cover image upload ─────────────────────────────────────────────────────
export async function uploadCover(file: File): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const path = `covers/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage
    .from('blog-media')
    .upload(path, file, { cacheControl: '31536000', upsert: false });
  if (error) throw error;
  const { data } = supabase.storage.from('blog-media').getPublicUrl(path);
  return data.publicUrl;
}
